import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { initKeyboardScrollNav } from "@/utils/keyboardScrollNav";

// Sep 2026 — global arrow-key scrolling for scrollable tables, lists,
// menus and dialogs (see utils/keyboardScrollNav.js).
initKeyboardScrollNav();

// Aggressively suppress ResizeObserver errors
// These are benign browser optimization errors that don't affect functionality
const suppressResizeObserverErrors = () => {
  const errorHandler = (event) => {
    if (
      event.message?.includes('ResizeObserver') ||
      event.error?.message?.includes('ResizeObserver')
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return true;
    }
  };

  window.addEventListener('error', errorHandler, true);
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes('ResizeObserver')) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }, true);

  // Override console.error to filter out ResizeObserver messages
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (args[0]?.toString().includes('ResizeObserver')) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
};

suppressResizeObserverErrors();

// ── ChunkLoadError handler ─────────────────────────────────────────
// When a new build is deployed while a user has an old tab open, the
// old JS references chunk hashes that no longer exist on the server.
// React.lazy() rejects with `ChunkLoadError` and the page goes white.
// Catch it at the window-level (both `error` and `unhandledrejection`
// fire for these depending on browser / async boundary) and force a
// one-shot reload to grab the new bundle. Use a sessionStorage flag
// so we don't end up in an infinite reload loop if the chunk URL is
// genuinely 404 or the CDN is down.
const handleChunkError = (label, source) => {
  const msg = source?.message || source?.reason?.message || '';
  const name = source?.name || source?.reason?.name || '';
  const isChunkError =
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk');
  if (!isChunkError) return false;

  const RELOAD_KEY = '__chunk_reload_attempted_at__';
  const last = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
  const now = Date.now();
  // Allow at most ONE reload per 60s — otherwise show a friendly
  // banner so the user can decide what to do (CDN may genuinely be
  // down, or they're offline).
  if (last && now - last < 60_000) {
    console.warn('[chunk-loader] reload already attempted recently; not retrying');
    // Render a fallback banner so the page isn't blank.
    if (!document.getElementById('__chunk_error_banner__')) {
      const banner = document.createElement('div');
      banner.id = '__chunk_error_banner__';
      banner.style.cssText =
        'position:fixed;top:0;left:0;right:0;background:#fef3c7;border-bottom:2px solid #f59e0b;'
        + 'padding:12px 20px;font-family:-apple-system,sans-serif;font-size:14px;color:#78350f;'
        + 'z-index:99999;text-align:center;';
      banner.innerHTML =
        '⚠ A new version of the app was deployed but a script failed to load. '
        + '<button id="__chunk_reload_btn__" style="margin-left:12px;padding:4px 12px;'
        + 'background:#f59e0b;color:white;border:none;border-radius:4px;cursor:pointer;">'
        + 'Reload now</button>';
      document.body && document.body.appendChild(banner);
      document.getElementById('__chunk_reload_btn__')?.addEventListener('click', () => {
        sessionStorage.removeItem(RELOAD_KEY);
        window.location.reload();
      });
    }
    return true;
  }
  sessionStorage.setItem(RELOAD_KEY, String(now));
  console.warn(`[chunk-loader] ${label} caught ChunkLoadError — reloading once`);
  window.location.reload();
  return true;
};

window.addEventListener('error', (e) => {
  handleChunkError('window.error', e.error || e);
}, true);
window.addEventListener('unhandledrejection', (e) => {
  handleChunkError('unhandledrejection', e);
}, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
